import {
  useRestApiProvider,
  UsePaginatedRequestHookParams,
  UsePaginatedRequestHookReturn,
} from "@jugl-web/rest-api";
import type { ClientContact } from "@jugl-web/rest-api/contacts";
import { getUniqueId } from "@jugl-web/utils";
import { useCallback, useEffect, useRef, useState } from "react";

export type UseContactsListParams = UsePaginatedRequestHookParams<{
  entityId: string;
  searchTerm?: string;
}>;

export type UseContactsListReturn =
  UsePaginatedRequestHookReturn<ClientContact> & { totalEntries: number };

export const useContactsList = ({
  entityId,
  searchTerm = "",
  pageSize = 20,
  skip,
}: UseContactsListParams): UseContactsListReturn => {
  const { contactsApi } = useRestApiProvider();
  const requestId = useRef<string>(undefined);

  const [contacts, setContacts] = useState<ClientContact[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState<number | null>(null);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [totalEntries, setTotalEntries] = useState(0);

  const [fetchContacts] = contactsApi.useLazyContactsListQuery();

  const loadPage = useCallback(
    async (page: number) => {
      try {
        setIsError(false);
        setIsLoading(true);

        const newRequestId = getUniqueId();
        requestId.current = newRequestId;

        const result = await fetchContacts({
          entityId,
          page,
          page_size: pageSize,
          searchTerm,
          meta: { silent: true },
        }).unwrap();

        if (requestId.current !== newRequestId) {
          return;
        }

        setTotalEntries(result.total_entries);

        setContacts((prev) => {
          if (page === 1) {
            return result.data;
          }

          const existingIds = new Set(prev.map((contact) => contact.id));
          const newContacts = result.data.filter(
            (contact) => !existingIds.has(contact.id)
          );

          return [...prev, ...newContacts];
        });

        setTotalPages(result.total_pages);
        setCurrentPage(page);
      } catch {
        setIsError(true);
      } finally {
        setIsLoading(false);
      }
    },
    [entityId, fetchContacts, pageSize, searchTerm]
  );

  const loadMore = useCallback(async () => {
    if (isLoading) {
      return;
    }
    if (totalPages !== null && currentPage >= totalPages) {
      return;
    }
    await loadPage(currentPage + 1);
  }, [currentPage, isLoading, loadPage, totalPages]);

  const reset = useCallback(() => {
    setContacts([]);
    setCurrentPage(1);
    setTotalPages(null);
    setIsError(false);
    loadPage(1);
  }, [loadPage]);

  const retry = useCallback(async () => {
    const pageToRetry =
      currentPage === 1 && contacts.length === 0 ? 1 : currentPage + 1;
    await loadPage(pageToRetry);
  }, [contacts.length, currentPage, loadPage]);

  const previousSearchTerm = useRef(searchTerm || "");
  const isInitialized = useRef(false);

  useEffect(() => {
    if (skip) {
      isInitialized.current = false;
      return;
    }

    const loadInitial = () => {
      setContacts([]);
      setCurrentPage(1);
      setTotalPages(null);
      setIsError(false);
      loadPage(1);
    };

    if (!isInitialized.current) {
      loadInitial();
      isInitialized.current = true;
      return;
    }

    if (
      previousSearchTerm.current === searchTerm ||
      (!previousSearchTerm.current && !searchTerm)
    ) {
      return;
    }

    previousSearchTerm.current = searchTerm;
    loadInitial();
  }, [loadPage, searchTerm, skip]);

  const hasMore = totalPages !== null && currentPage < totalPages;
  const isLoadingMore = isLoading && contacts.length > 0;
  const isLoadingMoreError = isError && contacts.length > 0;

  return {
    data: contacts,
    isLoading,
    isLoadingMore,
    isError,
    isLoadingMoreError,
    hasMore,
    loadMore,
    reset,
    retry,
    totalEntries,
  };
};
